import type { MaicUser, UserRole } from '../shell/TenantContext';

/**
 * Single source of truth for which roles must use MFA.
 *
 * These roles can read unmasked HIGH-PII and/or export/modify data, so MFA is
 * mandatory for them (ISO 27001 A.8.2 / A.8.3 / A.8.5). VIEWER is intentionally
 * absent: it is read-only with HIGH-PII masked, so a compromised viewer session
 * has minimal blast radius.
 *
 * To change the policy, edit only this set. NOTE: any new role is OPTIONAL by
 * default — if you add a privileged role, remember to list it here too.
 */
export const MFA_MANDATORY_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  'SUPERADMIN',
  'ADMIN',
  'DATA_ENGINEER',
  'ANALYST',
]);

/** True when MFA is mandatory for the given role. */
export function isMfaMandatory(role: UserRole | null | undefined): boolean {
  return role != null && MFA_MANDATORY_ROLES.has(role);
}

/** Demo and learning tenants hold synthetic data only — the MFA mandate
 *  exists to protect PII reads and data exports, neither of which these
 *  tenants have. Exempting them keeps client demos and PAI Academy Kitchen
 *  onboarding friction-free without weakening the policy where it matters.
 *  (Kitchen learners are provisioned as 'analyst', a mandatory-MFA role.) */
/** Keep in sync with backend/auth_service/mfa_policy.py — the backend is the
 *  enforced policy; this copy only controls the enrollment interstitial UX.
 *  The 8 seeded sales-demo tenants were added 2026-08-12 (EXC-MFA-01). */
const MFA_EXEMPT_TENANTS =
  /^tenant-ferco$|^tenant-ipsa$|^tenant-learn-|^tenant-(demo|finance|government|healthcare|itsm|manufacturing|procurement|travel)$/;

/** True when this user must be sent through MFA enrollment now: they hold a
 *  mandatory-MFA role and have not enrolled yet. Used to gate the app.
 *
 *  Exempt while impersonating (user.impersonated_by set): a superadmin must be
 *  able to impersonate any account regardless of the target's MFA enrollment
 *  status — the enrollment gate is for the account owner's own session, not
 *  for an admin briefly viewing as them. */
export function needsMfaEnrollment(user: MaicUser | null | undefined): boolean {
  if (!!user?.impersonated_by) return false;
  if (user?.tenant_id && MFA_EXEMPT_TENANTS.test(user.tenant_id)) return false;
  // The second factor already happened — at the customer's own identity
  // provider, verified server-side before this claim was minted
  // (backend/auth_service/sso_policy.py, mfa_mode 'delegated'). Asking such a
  // user to also enroll a platform TOTP is friction with no security gain, and
  // it is unskippable: this interstitial blocks the whole app.
  //
  // This is NOT the exemption that matters. The backend decides independently
  // via the `mfa_pending` claim; if it disagrees, the session is restricted no
  // matter what this returns, and the user cannot reach anything anyway.
  if (user?.mfa_source === 'idp') return false;
  return !!user && isMfaMandatory(user.role) && !user.mfa_enabled;
}
